import type { AndroidSymbol, SymbolViewProps } from 'expo-symbols'
import { Text, View } from 'react-native'
import { useFonts } from 'expo-font'
import { SymbolView } from 'expo-symbols'
import regular from 'expo-symbols/androidWeights/regular'

import { colors } from '../../theme'

export type IconName = NonNullable<SymbolViewProps['name']>

export function symbolName(
  ios: NonNullable<Extract<IconName, { ios?: unknown }>['ios']>,
  android: NonNullable<Extract<IconName, { android?: unknown }>['android']>,
  web: AndroidSymbol = android,
): Extract<IconName, { ios?: unknown }> {
  return { ios, android, web }
}

/** Theme color keys used by Icon. Add hex for one-off overrides. */
export type IconColor = keyof typeof colors | (string & Record<never, never>)
export type MaterialIconName = 'notes'

const MATERIAL_SYMBOL_GLYPHS: Record<MaterialIconName, string> = {
  notes: String.fromCharCode(57964),
}

export type IconProps = {
  name: IconName
  size?: number
  /** Theme color key or hex. Defaults to ink. */
  color?: IconColor
  className?: string
  /**
   * Forces Material Symbols rendering on all platforms (including iOS).
   * Useful when the icon must match Android/web exactly.
   */
  materialName?: MaterialIconName
}

export function Icon(props: IconProps) {
  const { size = 24, color = 'ink', className, name, materialName } = props

  const resolvedColor = resolveIconColor(color)

  const icon =
    materialName == null ? (
      <SymbolView name={name} size={size} tintColor={resolvedColor} />
    ) : (
      <MaterialGlyph
        fallbackName={name}
        materialName={materialName}
        size={size}
        color={resolvedColor}
      />
    )

  if (className != null) {
    return <View className={className}>{icon}</View>
  }
  return icon
}

function resolveIconColor(color: IconColor): string {
  return color in colors
    ? (colors as Record<string, string>)[color]
    : (color as string)
}

type MaterialGlyphProps = {
  fallbackName: IconName
  materialName: MaterialIconName
  size: number
  color: string
}

function MaterialGlyph({
  fallbackName,
  materialName,
  size,
  color,
}: MaterialGlyphProps) {
  const glyph = MATERIAL_SYMBOL_GLYPHS[materialName]
  const [loaded, loadError] = useFonts({ [regular.name]: regular.font })
  const containerStyle = { width: size, height: size }
  const textStyle = {
    fontFamily: regular.name,
    color,
    fontSize: size,
    lineHeight: size,
  }

  if (!loaded || loadError != null) {
    return <SymbolView name={fallbackName} size={size} tintColor={color} />
  }

  return (
    <View style={containerStyle}>
      <Text style={textStyle}>{glyph}</Text>
    </View>
  )
}
